import { log, notImplemented } from '@yp/common/core.ts';
import { pare } from '@yp/common/identity.ts';
import { HotSpotter } from '@yp/common/perf/hotspotter.ts';
import type { Annotated, MaybeAnnotated } from '@yp/common/schema/annotations.ts';
import { ErrorCode, handleFireError } from '@yp/node.apis.gcloud/errors.ts';
import { BROWSER, DEV } from 'esm-env';
import type {
  DocumentData,
  SetOptions as FireSetOptions,
  FirestoreDataConverter,
  PartialWithFieldValue,
  UpdateData,
  WithFieldValue,
  WriteBatch,
} from 'firebase/firestore';

const forceDryRun: true | undefined = undefined;
const devVerbosity = 0; // 1; // 2;
export const Verbosity: number = forceDryRun ? 1 : DEV ? devVerbosity : 0;

// Notoriously, a query may be incomplete in this way:
// (1) Create a query or collection.
// (2) Create a subquery of (1) and fetch it.
// (3) The result of (2) is considered a cached version of both (2) AND (1), since it's a subset
// of (1). This makes instantaneous "gets" (subscribe, store, unsubscribe) useless for (1).
// This is less misleading in the document case, but still fails the "get" workflow.
// DNS:TODO: See if we can identify this case specifically instead of ignoring all caching.
export const IgnoreFromCache = true;

const hotspotter = new HotSpotter({
  disableReports: !DEV,
  disableExceptions: !BROWSER,
  exceptionalReads: 10,
  exceptionalWrites: 5,
});

export function ensureAnnotatedRef<T extends { path: string }>(
  ref: MaybeAnnotated<T>,
): Annotated<T> {
  ref.annotation ??= ref.path;
  return ref as Annotated<T>;
}

// Relaxed or pedantic annotation type. Recommend relaxed in general, occasionally switching to
// pedantic to audit current callers.
type Ann<T> = Annotated<T>;
// type Ann<T> = MaybeAnnotated<T>;

interface CollectionLike {
  readonly path: string;
}

interface DocLike {
  readonly path: string;
  readonly converter?: FirestoreDataConverter<unknown, DocumentData> | null;
  // https://cloud.google.com/nodejs/docs/reference/firestore/latest/firestore/documentreference#properties
  // eslint-disable-next-line @typescript-eslint/naming-convention
  readonly _converter?: FirestoreDataConverter<unknown, DocumentData> | null;
}

export function docRef<R extends DocLike>(path: string, refer: (path: string) => R) {
  return refer(path);
}

export function addDoc<R extends CollectionLike, D extends DocLike, T>(
  ref: Ann<R>,
  value: WithFieldValue<T>,
  adder: (ref: R, value: WithFieldValue<T>) => Promise<D>,
  options?: { dryRun?: boolean },
) {
  const dryRun = options?.dryRun ?? forceDryRun;
  if (dryRun || Verbosity > 0) {
    log('addDoc', pare({ dryRun, path: ref.path, value }));
  }
  if (dryRun) {
    const id = 'dryRunId';
    return Promise.resolve({ path: `${ref.path}/${id}`, id });
  }
  hotspotter.writeEvent(ref.annotation ?? ref.path);
  return adder(ref, value);
}

export function getDoc<R extends DocLike, T>(ref: Ann<R>, getter: (ref: R) => Promise<T>) {
  hotspotter.readEvent(ref.annotation ?? ref.path);
  const result = getter(ref);
  if (Verbosity > 1) {
    return result.then((snapshot) => {
      log('getDoc', { doc: snapshot }, { prettyDepth: 4 });
      return snapshot;
    });
  }
  return result;
}

export function getDocs<R extends CollectionLike, T>(
  ref: Ann<R>,
  getter: (ref: R) => Promise<{ docs: T[] }>,
) {
  hotspotter.readEvent(ref.annotation ?? ref.path);
  return getter(ref).then((snapshot) => {
    if (Verbosity > 1) {
      log('getDocs', { count: snapshot.docs.length }, { prettyDepth: 4 });
    }
    return snapshot.docs;
  });
}

export type SetOptions = {
  // 'overwrite' is the default.
  mode?: 'overwrite' | 'merge';
  batch?: WriteBatch;
  dryRun?: boolean;
};

export function createDoc<R extends DocLike, T>(
  ref: Ann<R>,
  value: T,
  creater: (ref: R, value: T) => Promise<unknown>,
  options?: { dryRun?: boolean },
): Promise<unknown> {
  const dryRun = options?.dryRun ?? forceDryRun;
  if (dryRun || Verbosity > 0) {
    log('createDoc', pare({ dryRun, path: ref.path, value }));
  }
  if (dryRun) {
    return Promise.resolve();
  }
  hotspotter.writeEvent(ref.annotation ?? ref.path);
  return creater(ref, value);
}

export function setDoc<R extends DocLike, T>(
  ref: Ann<R>,
  value: T,
  setter: (ref: R, value: T, options: FireSetOptions) => Promise<unknown>,
  options?: SetOptions & { mode: 'overwrite' },
): Promise<unknown>;
export function setDoc<R extends DocLike, T>(
  ref: Ann<R>,
  value: PartialWithFieldValue<T>,
  setter: (ref: R, value: T, options: FireSetOptions) => Promise<unknown>,
  options: SetOptions & { mode: 'merge' },
): Promise<unknown>;
// export function setDoc<R extends DocLike, T>(
//   ref: Ann<R>,
//   value: Partial<T>,
//   setter: (ref: R, value: Partial<T>, options: FireSetOptions) => Promise<unknown>,
//   options: SetOptions & { mode: 'merge' },
// ): Promise<unknown>;
// export function setDoc<R extends DocLike, T>(
//   ref: Ann<R>,
//   value: Partial<T>,
//   setter: (ref: R, value: Partial<T>, options: FireSetOptions) => Promise<unknown>,
//   options?: SetOptions,
// ): Promise<unknown> {
export function setDoc<R extends DocLike, T>(
  ref: Ann<R>,
  value: PartialWithFieldValue<T>,
  setter: (ref: R, value: PartialWithFieldValue<T>, options: FireSetOptions) => Promise<unknown>,
  options?: SetOptions,
): Promise<unknown> {
  const setOptions = options?.mode === 'merge' ? { merge: true } : {};
  const dryRun = options?.dryRun ?? forceDryRun;
  if (dryRun || Verbosity > 0) {
    log('setDoc', pare({ dryRun, path: ref.path, value, setOptions }));
  }
  if (dryRun) {
    return Promise.resolve();
  }
  hotspotter.writeEvent(ref.annotation ?? ref.path);
  if (options?.batch) {
    notImplemented();
  } else {
    return setter(ref, value, setOptions);
  }
}

export async function updateDoc<R extends DocLike, T extends DocumentData>(
  ref: Ann<R>,
  value: PartialWithFieldValue<T>,
  updater: (ref: R, value: UpdateData<T>) => Promise<unknown>,
  options?: { dryRun?: boolean },
) {
  let data: UpdateData<T>;
  const converter = ref.converter ?? ref._converter;
  if (converter) {
    data = converter.toFirestore(value, { merge: true }) as UpdateData<T>;
  } else {
    data = value as UpdateData<T>;
  }
  const dryRun = options?.dryRun ?? forceDryRun;
  if (dryRun || Verbosity > 0) {
    log('updateDoc', pare({ dryRun, path: ref.path, data }), { verbosity: 'max' });
  }
  if (dryRun) {
    return Promise.resolve(true);
  }
  hotspotter.writeEvent(ref.annotation ?? ref.path);
  try {
    await updater(ref, data);
    return true;
  } catch (err) {
    handleFireError(err, 'not-found', ErrorCode.notFound);
    return false;
  }
}

export function deleteDoc<R extends DocLike, _T>(
  ref: Ann<R>,
  deleter: (ref: R) => Promise<unknown>,
  options?: { dryRun?: boolean },
) {
  // DNS: Recursively delete subcollections.
  const dryRun = options?.dryRun ?? forceDryRun;
  if (dryRun || Verbosity > 0) {
    log('deleteDoc', pare({ dryRun, path: ref.path }));
  }
  if (dryRun) {
    return Promise.resolve();
  }
  hotspotter.writeEvent(ref.annotation ?? ref.path);
  return deleter(ref);
}
